<template>
  <div class="card photo-gallery">
    <div class="flex gap-3 justify-between row-wrapper">
      <h3 class="flex flex-col md:flex-row md:gap-1 page-header-2 mb-6">
        Photos
        <span class="title-hint-text">(Click to view full size)</span>
      </h3>
      <div class="flex flex-col items-end md:flex-row w-6/12 md:w-auto">
        <span class="font-sm mr-2"><b>Max size per photo:</b> 20Mb</span>
        <div class="whitespace-nowrap">
          <SeraButton icon="fa-solid fa-images" @click="toggleFileInput">
            Upload Photo(s)
            <input
              ref="fileInput"
              type="file"
              accept=".jpg, .png, .jpeg"
              multiple
              class="hidden"
              @change="handleUpload"
            />
          </SeraButton>
        </div>
      </div>
    </div>
    <div class="card-body p-0 grid grid-cols-2 md:grid-cols-6 gap-3">
      <div v-for="photo in photos" :key="photo.id" class="photo-container">
        <a :href="photo.file.url" class="img-container">
          <div
            :style="{
              'background-image': `url(${imageSrc(photo)})`
            }"
            class="background-image"
          />
        </a>
        <div class="buttons-container">
          <Tooltip placement="top" :disabled="breakpoints().sm">
            <template #trigger>
              <SeraButton
                icon="fa-regular fa-trash-alt"
                @click="handleDelete($event, photo.id)"
              />
            </template>
            <template #tooltip> Delete Photo </template>
          </Tooltip>
          <span class="bottom-separator"> | </span>
          <Tooltip placement="top">
            <template #trigger>
              <SeraButton
                :icon="getVisibilityIcon(photo.customer_visible)"
                :class="`tooltip-photo-${photo.id}`"
                @click="handleVisibilityToggle(photo)"
              />
            </template>
            <template #tooltip>
              {{ getVisibilityTooltip(photo.customer_visible) }}
            </template>
          </Tooltip>
        </div>
        <div class="date-created mt-3">{{ datetime(photo.created_at) }}</div>
      </div>
    </div>
    <Modal
      v-if="deleteConfirm"
      title="Remove Photo"
      @hide-modal="toggleDeleteConfirm"
    >
      <div>
        This photo will be permanently deleted and you will not be able to undo
        this action. Are you sure?
      </div>
      <template #submit>
        <SeraButton id="submit_modal_btn" type="danger" @click="removePhoto()">
          Remove Photo
        </SeraButton>
      </template>
    </Modal>
    <PhotoUploadProgressModal
      v-if="uploading"
      :progress="progress"
      :photo-count="selectedPhotos.length"
      :total-file-size="totalFileSize"
      @hide-modal="handleCancel"
    ></PhotoUploadProgressModal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import PhotoUpload from "@/PhotoGallery/PhotoUpload";
import PhotoUploadProgressModal from "./PhotoUploadProgressModal.vue";

const MAX_PHOTO_COUNT = 20;

export default {
  components: {
    Modal,
    SeraButton,
    PhotoUploadProgressModal
  },
  props: {
    photos: { type: Array, default: () => [] },
    jobId: { type: Number, default: null },
    uploadLink: { type: Object, default: null, required: true }
  },
  emits: ["delete-success", "upload-success"],
  data: function () {
    return {
      deleteId: null,
      deleteConfirm: false,
      uploading: false,
      selectedPhotos: [],
      progress: 0
    };
  },
  computed: {
    totalFileSize() {
      return this.selectedPhotos.reduce((total, photo) => {
        total = total + photo.file.size;
        return total;
      }, 0);
    }
  },
  mounted() {
    EventBus.$on("job-photo-upload-complete", this.handleUploadComplete);
    window.onbeforeunload = (e) => {
      if (this.uploading) {
        e.preventDefault();
        return "Your pending photos will not be uploaded. Are you sure?";
      }
    };
  },
  beforeUnmount() {
    EventBus.$off("job-photo-upload-complete", this.handleUploadComplete);
    window.onbeforeunload = null;
  },
  methods: {
    ...mapActions(["deleteJobPhoto", "updateJobPhotoVisibility"]),
    toggleFileInput() {
      this.$refs.fileInput.click();
    },
    handleDelete(e, id) {
      e.preventDefault();
      e.stopPropagation();
      this.deleteId = id;
      this.toggleDeleteConfirm();
    },
    handleUpload(e) {
      if (e.target.files.length > MAX_PHOTO_COUNT) {
        return window.notify.error(
          `You can only upload a max of ${MAX_PHOTO_COUNT} photos at one time. Try splitting photos into multiple uploads.`
        );
      }
      const data = [];
      const handleLoad = (file) => {
        data.push({
          file: file,
          fileName: file.name
        });
        if (data.length === e.target.files.length) {
          this.uploadPhotos(data);
        }
      };
      // Loop through each file and add to store with base64 thumbnail
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = handleLoad(file);
      }
    },
    removePhoto() {
      const photoId = this.deleteId;

      this.deleteJobPhoto({ jobId: this.jobId, id: this.deleteId }).then(() => {
        this.deleteConfirm = false;

        this.$emit("delete-success", photoId);
      });
      this.deleteId = null;
    },
    toggleDeleteConfirm() {
      this.deleteConfirm = !this.deleteConfirm;
    },
    async uploadPhotos(photos) {
      this.uploading = true;
      this.selectedPhotos = photos;
      this.uploader = new PhotoUpload({ link: this.uploadLink, photos });
      this.uploader.onProgress = ({ progress }) => {
        this.progress = progress * 100;
      };
      await this.uploader.uploadAll();
      this.handleUploadComplete();
      this.$refs.fileInput.value = null;
    },
    handleUploadComplete() {
      if (!this.uploader) return;
      try {
        const { failures, success } = this.uploader;
        let message = `${success} photo(s) uploaded successfully!`;
        if (failures) {
          message = `${failures} out of ${
            success + failures
          } photo(s) failed to upload.<br/><br/>`;

          this.uploader.failedUploads.forEach((upload, i) => {
            message += `${i + 1}) ${upload.photo.fileName} ${this.fileSize(
              upload.photo.file
            )}<br/>`;
          });

          window.notify.error(message);
        } else {
          window.notify.success(message);
        }
        // Show notification on tab if in background
        if (document.hidden) {
          alert(message.replace(/<br\/>/gm, "\n"));
        }
      } catch (e) {
        // no-op
      }
      this.resetUploader();
    },
    handleCancel() {
      if (this.uploader) {
        this.uploader.onProgress = null;
        this.uploader.cancel();
        window.notify.error(
          `Cancelled uploading ${this.selectedPhotos.length} photo(s)`
        );
      }
      this.resetUploader();
    },
    resetUploader() {
      this.uploading = false;
      this.selectedPhotos = [];
      this.progress = 0;
      this.uploader = null;
      this.$emit("upload-success");
      // Fixes timing issue when uploading a single file that is too large
      setTimeout(() => {
        if ($(".modal")) {
          $(".modal").modal("hide");
          $(".modal").hide();
        }
      }, 750);
    },
    handleVisibilityToggle(photo) {
      const { id, customer_visible } = photo;

      const new_visibility = !customer_visible;

      const params = {
        jobId: this.jobId,
        photoId: id,
        customer_visible: new_visibility
      };

      this.updateJobPhotoVisibility(params).then(() => {
        photo.customer_visible = new_visibility;
      });
    },
    getVisibilityIcon(customerVisible) {
      const icon = customerVisible
        ? "fa-regular fa-eye"
        : "fa-regular fa-eye-slash";
      return icon;
    },
    getVisibilityTooltip(customerVisible) {
      return !customerVisible ? "Hidden from customer" : "Visible to customer";
    },
    imageSrc(photo) {
      if (!photo || !photo?.file) return "";
      const { file, created_at } = photo;
      let src = file.thumb?.url || file.url;
      if (moment().diff(moment(created_at), "minutes") < 2) {
        src = file.url;
      }
      return src;
    }
  }
};
</script>

<style scoped>
.background-image {
  width: 100%;
  background-position: center;
  min-height: 105px;
  background-repeat: no-repeat;
}

.date-created {
  text-align: center;
  position: relative;
  bottom: 30px;
}

.photo-container {
  text-align: center;
  display: inline-block;
  flex-direction: column;
  position: relative;
}

.img-container {
  display: flex;
  padding: 5px;
  background: var(--sera-color-neutral-gray-light-gray-100);
  border: 1px solid #e4e8eb;
  border-radius: 0.25rem;
}

.buttons-container {
  border: 1px solid var(--neutral-gray);
  display: flex;
  position: relative;
  bottom: 30px;
  flex-direction: row;
  align-items: center;
  height: 30px;
  background: rgba(255, 255, 255, 0.75);
  justify-content: space-evenly;
  border-radius: 0 0 4px 4px;
}
</style>
